import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
  selectSize?: 'sm' | 'md';
}

/** Styled native select — reliable, accessible; swap to Radix later if needed. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, options, placeholder, selectSize = 'md', id, ...props }, ref) => {
    const autoId = useId();
    const selectId = id ?? autoId;
    return (
      <div className={className}>
        {label && (
          <label htmlFor={selectId} className="label">
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            aria-invalid={error ? true : undefined}
            className={cn(
              'w-full appearance-none rounded-lg border bg-white text-secondary transition-all duration-200 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-grey-100',
              selectSize === 'sm' ? 'px-3 py-1.5 text-sm' : 'px-4 py-2.5 text-sm',
              // ⚠ AFTER the size classes, never before. `px-*` also sets
              // padding-right, so tailwind-merge was resolving `pr-9` away and
              // every long option ran under the chevron — "Jesse M" overlapped
              // it on /time's person picker at 390px, on both Week and Day.
              'pr-9',
              error
                ? 'border-error focus:border-error focus:ring-error/20'
                : 'border-grey-200 focus:border-accent focus:ring-accent/20',
            )}
            {...props}
          >
            {placeholder !== undefined && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-grey-400"
            aria-hidden
          />
        </div>
        {error && <p className="mt-1 text-caption text-error">{error}</p>}
      </div>
    );
  },
);
Select.displayName = 'Select';
